/*
 * File: DeploymentCard.tsx
 * Project: rosita
 * -----
 * Last Modified: 26 Feb 2022 10:52:07 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { Card, Col, Divider, Popover, Row, Space, Spin, Tooltip, Typography } from 'antd';
import { TimeZoneText } from 'components/common/TimeZoneText';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SparingRequestTypes } from 'models/SparingRequestTypes';
import { SparingRequest } from 'models/SparingRequests';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useGetSparingOrdersQuery, useGetTimeZoneInfoQuery } from 'redux/services/twittlebugs/sparingRequestsApi';
import { SalesOrderQuickViewTable } from '../SalesOrderQuickViewTable';
import { InstallLocation } from './components';
import { ShippingLocation } from './components/ShippingLocation';

const styles: InlineStylesModel = {
  blackFont: {
    color: '#555555'
  },
  bottomRightItem: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  deploymentStatus: {
    textAlign: 'right'
  },
  image: {
    height: '40px',
    opacity: 0.4
  },
  marginBottom: {},
  column: {
    display: 'flex',
    flexDirection: 'column'
  },
  text: {
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: 'top'
  },
  card: {
    borderRadius: 5
  },
  cardBody: {
    padding: 10,
    paddingRight: 15,
    paddingLeft: 15
  },
  largeText: {
    fontSize: '18px',
    fontFamily: 'Poppins',
    fontWeight: 700,
    color: '#000'
  },
  title: {
    fontSize: '15px',
    fontWeight: 500,
    color: '#636770'
  },
  subTitle: {
    fontSize: '15px',
    color: '#A0A3AE',
    minHeight: '27px'
  },
  smallText: {
    fontSize: '11px',
    color: 'lightGrey'
  },
  status: {
    fontSize: '16px',
    fontWeight: 700
  },
  draft: {
    color: 'orange'
  },
  posted: {
    color: '#4E937A'
  },
  soNumber: {
    fontSize: '14px'
  },
  poNumber: {
    fontSize: '14px',
    color: 'gray'
  },
  customerId: {
    fontSize: '16px',
    color: '#636770'
  },
  tooltipStyle: {
    fontSize: 12,
    textAlign: 'center'
  }
};

type Props = {
  sparingRequest: SparingRequest;
  sparingTypes?: SparingRequestTypes[];
};

export const DeploymentCard: React.FC<Props> = ({ sparingRequest, sparingTypes }) => {
  const nav = useNavigate();

  const formattedCreatedDate = moment(sparingRequest.createdDateTime).format('MM/DD/YYYY');

  const { data, isLoading } = useGetSparingOrdersQuery(sparingRequest.id, { skip: !sparingRequest.id });
  const { data: timeZoneData } = useGetTimeZoneInfoQuery(
    { latitude: sparingRequest?.shippingLocation?.latitude as number, longitude: sparingRequest?.shippingLocation?.longitude },
    { skip: !sparingRequest?.shippingLocation?.latitude || !sparingRequest?.shippingLocation?.longitude }
  );

  const sparingType = sparingTypes?.find((type) => type.id === sparingRequest.sparingRequestTypeId)?.name ?? 'n/a';

  const handleStatusMapping = (): { style: React.CSSProperties; text: string } => {
    switch (sparingRequest.status.toLowerCase()) {
      case 'draft':
        return { style: { ...styles.status, color: 'rgb(39, 134, 250)' }, text: 'Draft' };

      case 'cancelled':
        return { style: { ...styles.status, color: '#FF4F4F' }, text: 'Cancelled' };

      case 'postedwitherrors':
        return { style: { ...styles.status, color: 'green' }, text: 'Open w/ Errors' };

      case 'closed':
        return { style: { ...styles.status, color: 'black' }, text: 'Closed' };

      default:
        return { style: { ...styles.status, color: 'green' }, text: 'Open' };
    }
  };

  const handleDetailClick = (): void => {
    if (sparingRequest.status.toLowerCase() === 'Draft'.toLowerCase()) {
      nav(`/edit/${sparingRequest.id}`);
    } else {
      nav(`/posted/${sparingRequest.id}`);
    }
  };

  const requestNumberJSX = (
    <Tooltip title={sparingRequest.requestNumber}>
      <Typography.Text ellipsis style={styles.largeText}>
        {sparingRequest.requestNumber}
      </Typography.Text>
    </Tooltip>
  );

  const linkedCardJSX = (
    <>
      <div style={{ minHeight: 130 }}>
        <Row style={{ height: 80 }} gutter={8}>
          <Col xs={13} sm={12}>
            <Row style={{ marginBottom: 2 }}>{requestNumberJSX}</Row>
            <Row justify={'space-between'}>
              <Tooltip title="Primary Reference Number">
                <Typography.Text ellipsis>{sparingRequest.customerReferenceNumber}</Typography.Text>
              </Tooltip>
            </Row>
            <Row>
              <Tooltip title="Secondary Reference Number">
                <Typography.Text ellipsis>{sparingRequest.customerPurchaseOrderNumber}</Typography.Text>
              </Tooltip>
            </Row>
          </Col>
          <Col xs={11} sm={12}>
            <Row style={{ marginBottom: 2 }} justify="end">
              <Typography style={handleStatusMapping().style}>{handleStatusMapping().text}</Typography>
            </Row>
            <Row justify="end">
              <Col>
                <Typography style={styles.subTitle}>
                  <Tooltip title="Sparing Type" placement="right" autoAdjustOverflow={false} overlayInnerStyle={styles.tooltipStyle}>
                    {sparingType}
                  </Tooltip>
                </Typography>
              </Col>
            </Row>
          </Col>
        </Row>

        <Space style={{ width: '100%' }} direction="vertical" size={5}>
          <Row gutter={8} justify="space-between">
            <Col>
              <Typography style={styles.text}>
                <Tooltip title="Created By" placement="left" autoAdjustOverflow={false} overlayInnerStyle={styles.tooltipStyle}>
                  {sparingRequest.createdByFullName}
                </Tooltip>
              </Typography>
            </Col>
          </Row>
          <Row justify="space-between">
            <Col style={{ textAlign: 'end' }}>
              <Typography.Paragraph style={{ fontSize: '14px', color: 'gray' }}>
                <Tooltip title="Created Date" placement="bottom" autoAdjustOverflow={false} overlayInnerStyle={styles.tooltipStyle}>
                  {formattedCreatedDate}
                </Tooltip>
              </Typography.Paragraph>
            </Col>
            {sparingRequest.status !== 'Draft' && (
              <Col style={{ textAlign: 'end' }}>
                <Space>
                  <Popover
                    overlayStyle={{ width: 550, maxWidth: 'calc(100vw - 32px)' }}
                    content={
                      <SalesOrderQuickViewTable
                        timezoneData={timeZoneData}
                        size="small"
                        loading={isLoading}
                        style={{ width: 550, maxWidth: '100%' }}
                        scroll={{ x: 500 }}
                        pagination={false}
                        dataSource={data?.data}
                      />
                    }>
                    <Spin spinning={isLoading} indicator={<LoadingOutlined />}>
                      <InfoCircleOutlined style={{ color: 'rgb(39, 134, 250)' }} />
                    </Spin>
                  </Popover>
                  <Space direction="vertical" size={1}>
                    {isLoading && <Typography.Text style={{ fontSize: '14px', color: 'gray' }}>Loading...</Typography.Text>}
                    {data?.data[0].estimatedDeliveryDate && (
                      <TimeZoneText
                        deliveryStyle={{ fontSize: '14px', color: 'gray' }}
                        utcStyle={{ fontSize: '14px', color: 'gray' }}
                        dateTime={data?.data[0].estimatedDeliveryDate}
                        divider={false}
                        spaceDirection="vertical"
                        timezoneData={timeZoneData}
                        spaceSize={0}
                      />
                    )}
                    {!data?.data[0].estimatedDeliveryDate && !isLoading && <Typography.Text style={{ fontSize: '14px', color: 'gray' }}>TBD</Typography.Text>}
                  </Space>
                </Space>
              </Col>
            )}
          </Row>
        </Space>
      </div>
      <Divider />
      <Row style={{ minHeight: 80 }}>
        <Col style={{ textAlign: 'start' }} span={10}>
          <Typography.Paragraph style={{ fontSize: '14px', color: 'gray' }}>
            <Tooltip title="Outage Location" placement="top" autoAdjustOverflow={false} overlayInnerStyle={styles.tooltipStyle}>
              <InstallLocation deployment={sparingRequest} />
            </Tooltip>
          </Typography.Paragraph>
        </Col>

        <Col span={4}>
          <Row justify={'center'}>
            <Divider type="vertical" style={{ fontSize: 80 }} />
          </Row>
        </Col>
        <Col style={{ textAlign: 'end' }} span={10}>
          <Typography.Paragraph style={{ fontSize: '14px', color: 'gray' }}>
            <Tooltip title="Shipping Location" placement="top" autoAdjustOverflow={false} overlayInnerStyle={styles.tooltipStyle}>
              <ShippingLocation deployment={sparingRequest} />
            </Tooltip>
          </Typography.Paragraph>
        </Col>
      </Row>
    </>
  );

  return (
    <Card style={styles.card} onClick={handleDetailClick} bodyStyle={styles.cardBody} hoverable>
      {linkedCardJSX}
    </Card>
  );
};
